/**
 * KOPS-460: Event Management Admin UI
 * Create, edit, view events and registrations
 */

import React, { useState, useEffect } from 'react';
import {
  Card, Table, Button, Badge, Modal, Form, Row, Col, Alert, Spinner, Tab, Nav
} from 'react-bootstrap';
import {
  FaPlus, FaEdit, FaUsers, FaCalendarAlt, FaExternalLinkAlt,
  FaCopy, FaCheck, FaTrash, FaEye, FaEnvelope, FaPaperPlane
} from 'react-icons/fa';
import axiosInstance from '../../helpers/api';

interface EventData {
  id: string;
  title: string;
  series_name: string;
  category: string;
  description: string;
  event_date: string;
  event_time: string;
  duration_minutes: number;
  location_name: string;
  location_address: string;
  max_capacity: number;
  ticket_price: number;
  current_registrations: number;
  sponsor_name: string | null;
  sponsor_email: string | null;
  sponsor_contact_name: string | null;
  status: string;
  is_private: boolean;
}

interface Registration {
  id: string;
  full_name: string;
  email: string;
  phone: string;
  brokerage: string | null;
  license_number: string | null;
  status: string;
  confirmation_sent: boolean;
  attended: boolean;
  created_at: string;
}

const emptyForm = {
  title: '', seriesName: 'AI Without Fear', category: 'seminar', description: '',
  eventDate: '', eventTime: '', durationMinutes: '90',
  locationName: '', locationAddress: '', maxCapacity: '50', ticketPrice: '0',
  sponsorName: '', sponsorEmail: '', sponsorContactName: '', isPrivate: false
};

const EventManagement: React.FC = () => {
  const [events, setEvents] = useState<EventData[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRegsModal, setShowRegsModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<EventData | null>(null);
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [regsLoading, setRegsLoading] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [emailData, setEmailData] = useState({ subject: '', message: '' });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showAddReg, setShowAddReg] = useState(false);
  const [newReg, setNewReg] = useState({ fullName: '', email: '', phone: '', brokerage: '', licenseNumber: '', sendEmail: true });
  const [addingReg, setAddingReg] = useState(false);
  const [alert, setAlert] = useState<{ type: string; message: string } | null>(null);
  const [copiedId, setCopiedId] = useState('');

  useEffect(() => { fetchEvents(); }, []);

  const fetchEvents = async () => {
    try {
      const response = await axiosInstance.get('/events/all');
      if (response.data.success) {
        setEvents(response.data.events);
      }
    } catch (err) {
      console.error('Failed to fetch events:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRegistrations = async (eventId: string) => {
    setRegsLoading(true);
    try {
      const response = await axiosInstance.get(`/events/${eventId}/registrations`);
      if (response.data.success) {
        setRegistrations(response.data.registrations);
      }
    } catch (err) {
      console.error('Failed to fetch registrations:', err);
      setRegistrations([]);
    } finally {
      setRegsLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await axiosInstance.post('/events/create', {
        title: formData.title,
        seriesName: formData.seriesName,
        category: formData.category,
        description: formData.description,
        eventDate: formData.eventDate,
        eventTime: formData.eventTime,
        durationMinutes: parseInt(formData.durationMinutes),
        locationName: formData.locationName,
        locationAddress: formData.locationAddress,
        maxCapacity: parseInt(formData.maxCapacity),
        ticketPrice: parseFloat(formData.ticketPrice),
        sponsorName: formData.sponsorName || undefined,
        sponsorEmail: formData.sponsorEmail || undefined,
        sponsorContactName: formData.sponsorContactName || undefined,
        isPrivate: formData.isPrivate
      });

      if (response.data.success) {
        setAlert({ type: 'success', message: `Event created! Link: ${response.data.link}` });
        setShowCreateModal(false);
        setFormData(emptyForm);
        fetchEvents();
      }
    } catch (err: any) {
      setAlert({ type: 'danger', message: err.response?.data?.error || 'Failed to create event' });
    } finally {
      setSubmitting(false);
    }
  };

  const openEdit = (event: EventData) => {
    setSelectedEvent(event);
    const dt = new Date(event.event_date);
    const localDate = dt.getFullYear() + '-' + String(dt.getMonth()+1).padStart(2,'0') + '-' + String(dt.getDate()).padStart(2,'0') + 'T' + String(dt.getHours()).padStart(2,'0') + ':' + String(dt.getMinutes()).padStart(2,'0');
    setFormData({
      title: event.title,
      seriesName: event.series_name,
      category: event.category,
      description: event.description || '',
      eventDate: localDate,
      eventTime: event.event_time,
      durationMinutes: String(event.duration_minutes),
      locationName: event.location_name,
      locationAddress: event.location_address,
      maxCapacity: String(event.max_capacity),
      ticketPrice: String(event.ticket_price || 0),
      sponsorName: event.sponsor_name || '',
      sponsorEmail: event.sponsor_email || '',
      sponsorContactName: event.sponsor_contact_name || '',
      isPrivate: event.is_private
    });
    setShowEditModal(true);
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEvent) return;
    setSubmitting(true);
    try {
      const response = await axiosInstance.put(`/events/${selectedEvent.id}`, {
        title: formData.title,
        seriesName: formData.seriesName,
        category: formData.category,
        description: formData.description,
        eventDate: formData.eventDate,
        eventTime: formData.eventTime,
        durationMinutes: parseInt(formData.durationMinutes),
        locationName: formData.locationName,
        locationAddress: formData.locationAddress,
        maxCapacity: parseInt(formData.maxCapacity),
        ticketPrice: parseFloat(formData.ticketPrice),
        sponsorName: formData.sponsorName || undefined,
        sponsorEmail: formData.sponsorEmail || undefined,
        sponsorContactName: formData.sponsorContactName || undefined,
        isPrivate: formData.isPrivate
      });

      if (response.data.success) {
        setAlert({ type: 'success', message: 'Event updated!' });
        setShowEditModal(false);
        fetchEvents();
      }
    } catch (err: any) {
      setAlert({ type: 'danger', message: err.response?.data?.error || 'Failed to update event' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddRegistrant = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEvent) return;
    setAddingReg(true);
    try {
      await axiosInstance.post('/events/register', {
        eventId: selectedEvent.id,
        fullName: newReg.fullName,
        email: newReg.email,
        phone: newReg.phone,
        brokerage: newReg.brokerage || undefined,
        licenseNumber: newReg.licenseNumber || undefined,
        skipEmail: !newReg.sendEmail
      });
      setNewReg({ fullName: '', email: '', phone: '', brokerage: '', licenseNumber: '', sendEmail: true });
      setShowAddReg(false);
      fetchRegistrations(selectedEvent.id);
      fetchEvents();
      setAlert({ type: 'success', message: `${newReg.fullName} added and confirmation email sent!` });
    } catch (err: any) {
      setAlert({ type: 'danger', message: err.response?.data?.error || 'Failed to add registrant' });
    } finally {
      setAddingReg(false);
    }
  };

  const handleStatusChange = async (regId: string, newStatus: string) => {
    if (!selectedEvent) return;
    try {
      await axiosInstance.put(`/events/registration/${regId}/status`, { status: newStatus });
      fetchRegistrations(selectedEvent.id);
    } catch (err: any) {
      setAlert({ type: 'danger', message: err.response?.data?.error || 'Failed to update status' });
    }
  };

  const handleUnsubscribe = async (regId: string, email: string) => {
    if (!selectedEvent) return;
    try {
      await axiosInstance.put(`/events/registration/${regId}/unsubscribe`);
      fetchRegistrations(selectedEvent.id);
      setAlert({ type: 'info', message: `${email} unsubscribed from communications` });
    } catch (err: any) {
      setAlert({ type: 'danger', message: err.response?.data?.error || 'Failed to update' });
    }
  };

  const copyLink = (eventId: string) => {
    const link = `https://app.kairosiq.ai/events/${eventId}`;
    navigator.clipboard.writeText(link);
    setCopiedId(eventId);
    setTimeout(() => setCopiedId(''), 2000);
  };

  const openEmailBlast = () => {
    setEmailData({ subject: '', message: '' });
    setShowEmailModal(true);
  };

  const handleSendBlast = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEvent || registrations.length === 0) return;
    setSendingEmail(true);
    try {
      const response = await axiosInstance.post(`/events/${selectedEvent.id}/email-blast`, {
        subject: emailData.subject,
        message: emailData.message
      });
      if (response.data.success) {
        setAlert({ type: 'success', message: `Email sent to ${response.data.sent} registrants!` });
        setShowEmailModal(false);
      }
    } catch (err: any) {
      setAlert({ type: 'danger', message: err.response?.data?.error || 'Failed to send emails' });
    } finally {
      setSendingEmail(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const getCategoryBadge = (cat: string) => {
    switch (cat) {
      case 'workshop': return <Badge bg="warning" text="dark">Workshop</Badge>;
      case 'ce_course': return <Badge bg="info">CE Course</Badge>;
      default: return <Badge bg="primary">Seminar</Badge>;
    }
  };

  return (
    <div>
      {alert && (
        <Alert variant={alert.type} dismissible onClose={() => setAlert(null)} className="mb-3">
          {alert.message}
        </Alert>
      )}

      <Card className="mb-3">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0"><FaCalendarAlt className="me-2" />Event Management</h5>
          <Button variant="primary" size="sm" onClick={() => { setFormData(emptyForm); setShowCreateModal(true); }}>
            <FaPlus className="me-1" /> New Event
          </Button>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="text-center py-4"><Spinner animation="border" /></div>
          ) : events.length === 0 ? (
            <p className="text-muted text-center py-4">No events yet. Create your first event!</p>
          ) : (
            <Table responsive hover size="sm">
              <thead>
                <tr>
                  <th style={{ minWidth: '280px' }}>Event</th>
                  <th style={{ minWidth: '140px' }}>Date & Time</th>
                  <th>Details</th>
                  <th style={{ minWidth: '130px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {events.map(event => (
                  <tr key={event.id}>
                    <td style={{ paddingLeft: '16px' }}>
                      <strong>{event.title}</strong>
                      <br />
                      <small className="text-muted">{event.location_name} — {event.location_address}</small>
                    </td>
                    <td>
                      {formatDate(event.event_date)}
                      <br />
                      <small className="text-muted">{event.event_time}</small>
                    </td>
                    <td>
                      <div className="d-flex flex-wrap gap-1">
                        {getCategoryBadge(event.category)}
                        {event.ticket_price > 0
                          ? <Badge bg="warning" text="dark">${event.ticket_price}</Badge>
                          : <Badge bg="light" text="dark">Free</Badge>
                        }
                        <Badge bg={event.current_registrations >= event.max_capacity ? 'danger' : 'success'}>
                          {event.current_registrations}/{event.max_capacity}
                        </Badge>
                        {event.is_private
                          ? <Badge bg="secondary">Private</Badge>
                          : <Badge bg="info">Public</Badge>
                        }
                        {event.sponsor_name && <Badge bg="outline-dark" className="border">{event.sponsor_name}</Badge>}
                      </div>
                    </td>
                    <td>
                      <Button
                        variant="outline-primary" size="sm" className="me-1 mb-1"
                        title="View Registrations"
                        onClick={() => { setSelectedEvent(event); fetchRegistrations(event.id); setShowRegsModal(true); }}
                      >
                        <FaUsers />
                      </Button>
                      <Button
                        variant="outline-warning" size="sm" className="me-1 mb-1"
                        title="Edit Event"
                        onClick={() => openEdit(event)}
                      >
                        <FaEdit />
                      </Button>
                      <Button
                        variant="outline-secondary" size="sm" className="me-1 mb-1"
                        title="Copy Registration Link"
                        onClick={() => copyLink(event.id)}
                      >
                        {copiedId === event.id ? <FaCheck className="text-success" /> : <FaCopy />}
                      </Button>
                      <Button
                        variant="outline-info" size="sm" className="mb-1"
                        title="Preview on AISearchPortal"
                        onClick={() => window.open(`https://app.kairosiq.ai/events/${event.id}`, '_blank')}
                      >
                        <FaExternalLinkAlt />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Create Event Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Create New Event</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreate}>
          <Modal.Body>
            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Event Title *</Form.Label>
                  <Form.Control required value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} placeholder="AI Foundations Without Fear: Hands-On Workshop" />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Category *</Form.Label>
                  <Form.Select value={formData.category} onChange={e => setFormData({ ...formData, category: e.target.value })}>
                    <option value="seminar">Seminar (Free)</option>
                    <option value="workshop">Workshop (Paid)</option>
                    <option value="ce_course">CE Course</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control as="textarea" rows={3} value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} />
            </Form.Group>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Date *</Form.Label>
                  <Form.Control required type="datetime-local" value={formData.eventDate} onChange={e => setFormData({ ...formData, eventDate: e.target.value })} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Display Time *</Form.Label>
                  <Form.Control required value={formData.eventTime} onChange={e => setFormData({ ...formData, eventTime: e.target.value })} placeholder="10:00 AM - 1:00 PM MST" />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Duration (min)</Form.Label>
                  <Form.Control type="number" value={formData.durationMinutes} onChange={e => setFormData({ ...formData, durationMinutes: e.target.value })} />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Location Name *</Form.Label>
                  <Form.Control required value={formData.locationName} onChange={e => setFormData({ ...formData, locationName: e.target.value })} placeholder="KairosIQ AI Headquarters" />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Address *</Form.Label>
                  <Form.Control required value={formData.locationAddress} onChange={e => setFormData({ ...formData, locationAddress: e.target.value })} placeholder="2700 S Gilbert Rd Ste 5, Chandler, AZ 85286" />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Max Capacity</Form.Label>
                  <Form.Control type="number" value={formData.maxCapacity} onChange={e => setFormData({ ...formData, maxCapacity: e.target.value })} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Ticket Price ($)</Form.Label>
                  <Form.Control type="number" step="0.01" value={formData.ticketPrice} onChange={e => setFormData({ ...formData, ticketPrice: e.target.value })} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Series Name</Form.Label>
                  <Form.Control value={formData.seriesName} onChange={e => setFormData({ ...formData, seriesName: e.target.value })} />
                </Form.Group>
              </Col>
            </Row>

            <hr />
            <h6>Sponsor / Host (Optional)</h6>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Sponsor Name</Form.Label>
                  <Form.Control value={formData.sponsorName} onChange={e => setFormData({ ...formData, sponsorName: e.target.value })} placeholder="Landmark Title" />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Sponsor Contact</Form.Label>
                  <Form.Control value={formData.sponsorContactName} onChange={e => setFormData({ ...formData, sponsorContactName: e.target.value })} placeholder="Beth Clauser" />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Sponsor Email</Form.Label>
                  <Form.Control type="email" value={formData.sponsorEmail} onChange={e => setFormData({ ...formData, sponsorEmail: e.target.value })} placeholder="beth@landmarktitle.com" />
                </Form.Group>
              </Col>
            </Row>

            <Form.Check
              type="checkbox"
              id="is-private"
              label="Private event (only accessible via direct link, hidden from public events page)"
              checked={formData.isPrivate}
              onChange={e => setFormData({ ...formData, isPrivate: e.target.checked })}
              className="mb-2"
            />
            <small className="text-muted">Events with a sponsor are automatically set to private.</small>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={submitting}>
              {submitting ? <><Spinner size="sm" className="me-1" />Creating...</> : 'Create Event'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Edit Event Modal — reuses same form as Create */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Event</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleEdit}>
          <Modal.Body>
            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Event Title *</Form.Label>
                  <Form.Control required value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Category *</Form.Label>
                  <Form.Select value={formData.category} onChange={e => setFormData({ ...formData, category: e.target.value })}>
                    <option value="seminar">Seminar (Free)</option>
                    <option value="workshop">Workshop (Paid)</option>
                    <option value="ce_course">CE Course</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control as="textarea" rows={3} value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} />
            </Form.Group>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Date *</Form.Label>
                  <Form.Control required type="datetime-local" value={formData.eventDate} onChange={e => setFormData({ ...formData, eventDate: e.target.value })} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Display Time *</Form.Label>
                  <Form.Control required value={formData.eventTime} onChange={e => setFormData({ ...formData, eventTime: e.target.value })} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Duration (min)</Form.Label>
                  <Form.Control type="number" value={formData.durationMinutes} onChange={e => setFormData({ ...formData, durationMinutes: e.target.value })} />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Location Name *</Form.Label>
                  <Form.Control required value={formData.locationName} onChange={e => setFormData({ ...formData, locationName: e.target.value })} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Address *</Form.Label>
                  <Form.Control required value={formData.locationAddress} onChange={e => setFormData({ ...formData, locationAddress: e.target.value })} />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Max Capacity</Form.Label>
                  <Form.Control type="number" value={formData.maxCapacity} onChange={e => setFormData({ ...formData, maxCapacity: e.target.value })} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Ticket Price ($)</Form.Label>
                  <Form.Control type="number" step="0.01" value={formData.ticketPrice} onChange={e => setFormData({ ...formData, ticketPrice: e.target.value })} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Series Name</Form.Label>
                  <Form.Control value={formData.seriesName} onChange={e => setFormData({ ...formData, seriesName: e.target.value })} />
                </Form.Group>
              </Col>
            </Row>
            <hr />
            <h6>Sponsor / Host (Optional)</h6>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Sponsor Name</Form.Label>
                  <Form.Control value={formData.sponsorName} onChange={e => setFormData({ ...formData, sponsorName: e.target.value })} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Sponsor Contact</Form.Label>
                  <Form.Control value={formData.sponsorContactName} onChange={e => setFormData({ ...formData, sponsorContactName: e.target.value })} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Sponsor Email</Form.Label>
                  <Form.Control type="email" value={formData.sponsorEmail} onChange={e => setFormData({ ...formData, sponsorEmail: e.target.value })} />
                </Form.Group>
              </Col>
            </Row>
            <Form.Check type="checkbox" id="is-private-edit" label="Private event (only accessible via direct link)" checked={formData.isPrivate} onChange={e => setFormData({ ...formData, isPrivate: e.target.checked })} className="mb-2" />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditModal(false)}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={submitting}>
              {submitting ? <><Spinner size="sm" className="me-1" />Saving...</> : 'Save Changes'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Registrations Modal */}
      <Modal show={showRegsModal} onHide={() => setShowRegsModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>
            Registrations: {selectedEvent?.title}
            <br />
            <small className="text-muted">{selectedEvent ? formatDate(selectedEvent.event_date) : ''} | {selectedEvent?.event_time}</small>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Add Registrant Form */}
          <div className="mb-3">
            {!showAddReg ? (
              <Button variant="outline-primary" size="sm" onClick={() => setShowAddReg(true)}>
                <FaPlus className="me-1" /> Add Registrant
              </Button>
            ) : (
              <Card className="border-primary">
                <Card.Body>
                  <Form onSubmit={handleAddRegistrant}>
                    <Row>
                      <Col md={3}>
                        <Form.Control required size="sm" placeholder="Full Name *" value={newReg.fullName} onChange={e => setNewReg({ ...newReg, fullName: e.target.value })} className="mb-2" />
                      </Col>
                      <Col md={3}>
                        <Form.Control required size="sm" type="email" placeholder="Email *" value={newReg.email} onChange={e => setNewReg({ ...newReg, email: e.target.value })} className="mb-2" />
                      </Col>
                      <Col md={2}>
                        <Form.Control required size="sm" placeholder="Phone *" value={newReg.phone} onChange={e => setNewReg({ ...newReg, phone: e.target.value })} className="mb-2" />
                      </Col>
                      <Col md={2}>
                        <Form.Control size="sm" placeholder="Brokerage" value={newReg.brokerage} onChange={e => setNewReg({ ...newReg, brokerage: e.target.value })} className="mb-2" />
                      </Col>
                      <Col md={4}>
                        <div className="d-flex gap-2 align-items-center">
                          <Form.Check type="checkbox" id="send-email-check" label="Send confirmation" checked={newReg.sendEmail} onChange={e => setNewReg({ ...newReg, sendEmail: e.target.checked })} style={{ fontSize: '12px' }} />
                          <Button type="submit" variant="primary" size="sm" disabled={addingReg}>
                            {addingReg ? <Spinner size="sm" /> : 'Add'}
                          </Button>
                          <Button variant="outline-secondary" size="sm" onClick={() => setShowAddReg(false)}>Cancel</Button>
                        </div>
                      </Col>
                    </Row>
                  </Form>
                </Card.Body>
              </Card>
            )}
          </div>

          {regsLoading ? (
            <div className="text-center py-4"><Spinner animation="border" /></div>
          ) : registrations.length === 0 ? (
            <p className="text-muted text-center py-4">No registrations yet. Use "Add Registrant" above to add manually.</p>
          ) : (
            <>
              <p className="mb-3">
                <strong>{registrations.length}</strong> registered out of <strong>{selectedEvent?.max_capacity}</strong> capacity
                {selectedEvent?.ticket_price && selectedEvent.ticket_price > 0 && (
                  <span className="ms-3">
                    <Badge bg="success">Revenue: ${(registrations.length * selectedEvent.ticket_price).toFixed(2)}</Badge>
                  </span>
                )}
              </p>
              <Table responsive hover size="sm">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Brokerage</th>
                    <th>Status</th>
                    <th>Registered</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {registrations.map((reg, i) => (
                    <tr key={reg.id} className={reg.status === 'unsubscribed' ? 'text-muted' : ''}>
                      <td>{i + 1}</td>
                      <td><strong>{reg.full_name}</strong></td>
                      <td><a href={`mailto:${reg.email}`}>{reg.email}</a></td>
                      <td>{reg.phone}</td>
                      <td>{reg.brokerage || '—'}</td>
                      <td>
                        <Form.Select
                          size="sm"
                          value={reg.attended ? 'attended' : reg.status}
                          onChange={e => handleStatusChange(reg.id, e.target.value)}
                          style={{ fontSize: '12px', width: '120px' }}
                        >
                          <option value="registered">Registered</option>
                          <option value="confirmed">Confirmed</option>
                          <option value="attended">Attended</option>
                          <option value="no_show">No Show</option>
                          <option value="cancelled">Cancelled</option>
                        </Form.Select>
                      </td>
                      <td>{new Date(reg.created_at).toLocaleDateString()}</td>
                      <td>
                        {reg.status !== 'unsubscribed' && (
                          <Button variant="outline-danger" size="sm" title="Unsubscribe" onClick={() => handleUnsubscribe(reg.id, reg.email)}>
                            <FaTrash />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {registrations.length > 0 && (
            <Button variant="primary" onClick={openEmailBlast}>
              <FaEnvelope className="me-1" /> Email All ({registrations.length})
            </Button>
          )}
          <Button variant="secondary" onClick={() => setShowRegsModal(false)}>Close</Button>
        </Modal.Footer>
      </Modal>
      {/* Email Blast Modal */}
      <Modal show={showEmailModal} onHide={() => setShowEmailModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Email Registrants
            <br />
            <small className="text-muted">{selectedEvent?.title} — {registrations.length} recipients</small>
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSendBlast}>
          <Modal.Body>
            <Alert variant="info" className="mb-3">
              This will send a branded email to all {registrations.length} registrants of <strong>{selectedEvent?.title}</strong> via AI Search Portal.
            </Alert>

            <Form.Group className="mb-3">
              <Form.Label><strong>Subject Line *</strong></Form.Label>
              <Form.Control
                required
                value={emailData.subject}
                onChange={e => setEmailData({ ...emailData, subject: e.target.value })}
                placeholder="e.g., You're Invited: Exclusive Follow-Up Session This Thursday"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label><strong>Message *</strong></Form.Label>
              <Form.Control
                required
                as="textarea"
                rows={8}
                value={emailData.message}
                onChange={e => setEmailData({ ...emailData, message: e.target.value })}
                placeholder={"Hi {{name}},\n\nThank you for attending our session! We'd like to invite you to...\n\nUse {{name}} to personalize each email with the recipient's name."}
              />
              <Form.Text className="text-muted">
                Use <code>{'{{name}}'}</code> to insert the recipient's name. Line breaks will be preserved. The message will be wrapped in the AI Search Portal branded email template.
              </Form.Text>
            </Form.Group>

            <div className="p-3 bg-light border rounded">
              <small className="text-muted d-block mb-2"><strong>Preview:</strong></small>
              <div style={{ fontFamily: 'Arial, sans-serif', fontSize: '13px' }}>
                <div style={{ background: '#0F1E35', color: 'white', padding: '15px', textAlign: 'center', borderRadius: '4px 4px 0 0' }}>
                  <strong style={{ letterSpacing: '1px' }}>AI Without Fear</strong>
                </div>
                <div style={{ padding: '15px', border: '1px solid #ddd', borderTop: 'none', borderRadius: '0 0 4px 4px' }}>
                  <p style={{ margin: '0 0 8px' }}><strong>Subject:</strong> {emailData.subject || '(enter subject above)'}</p>
                  <hr />
                  <div style={{ whiteSpace: 'pre-wrap' }}>{(emailData.message || '(enter message above)').replace(/\{\{name\}\}/g, 'Jane Smith')}</div>
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEmailModal(false)}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={sendingEmail || !emailData.subject || !emailData.message}>
              {sendingEmail ? <><Spinner size="sm" className="me-1" />Sending to {registrations.length} people...</> : <><FaPaperPlane className="me-1" /> Send to {registrations.length} Registrants</>}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default EventManagement;
